import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from "react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "framer-motion"
import { RemoveScroll } from "react-remove-scroll"
import type { CastRow, LocationRow, PropRow } from "@/components/prompt-editor/mention-sources"
import type { ScenePrimitiveShape } from "@/lib/location-scene-3d/scene-state"
import type { CanvasReferenceItem } from "@/types/canvas"
import { insideDialogLayer, SCENE_3D_PICKER_ATTR } from "@/lib/dialog-layer"
import { cn } from "@/lib/utils"
import { ReferencePickerContent } from "./reference-picker-content"
import type { PickerTab } from "./reference-picker-types"
import { useReferencePickerState } from "./use-reference-picker-state"

export type ReferencePickerDirection = "up" | "down"

export type ReferencePickerPlacement =
  | { mode: "anchored"; width?: "container" | "2x"; direction?: ReferencePickerDirection }
  | { mode: "embedded"; direction?: ReferencePickerDirection }

export type ReferencePickerDrawerProps = {
  open: boolean
  onClose: () => void
  placement?: ReferencePickerPlacement
  characters: CastRow[]
  locations: LocationRow[]
  props: PropRow[]
  existingKeys: string[]
  onPickReference: (ref: CanvasReferenceItem) => void | Promise<void>
  /** Scene primitives tab — add shape without going through CanvasReferenceItem. */
  onPickPrimitive?: (shape: ScenePrimitiveShape) => void
  allowedMediaTypes?: ("image" | "video" | "audio")[]
  enableDrag?: boolean
  outsideClickIgnoreRefs?: RefObject<HTMLElement | null>[]
  /**
   * Portal measure target (e.g. AddDashedTile). Falls back to host.parentElement.
   */
  anchorRef?: RefObject<HTMLElement | null>
  onUploadClick?: () => void
  uploadUploading?: boolean
  onLibraryPickClick?: () => void
  onSketchClick?: () => void
  onSketchEditRef?: (ref: CanvasReferenceItem) => void
  onPreviewRef?: (ref: CanvasReferenceItem) => void
  /** Z for built-in video lightbox when opened from this drawer. */
  previewLightboxNested?: boolean | 2 | 3
  tabs?: PickerTab[]
  entityPick?: boolean
  title?: string
  subtitle?: string
  /**
   * When the drawer lives inside a WorkspaceDialog, dialog-layer clicks would
   * otherwise never close it. Set true so only the panel + ignoreRefs keep it open.
   */
  closeOnDialogBackgroundClick?: boolean
  /**
   * Portal anchored panel to document.body at this z-index (above 3D scene stage).
   * Position tracks the relative parent of the mount point.
   */
  portalZIndex?: number
}

const PANEL_SURFACE_CLASS =
  "overflow-hidden rounded-lg border border-ui-border shadow-lg ring-1 shadow-black/25 ring-black/5 backdrop-blur-md dark:bg-zinc-950/80 light:bg-ui-bg-elevated/88"

const GAP_PX = 8
const VIEWPORT_MARGIN_PX = 8
/** Cap portaled panel so full-bleed strip anchors stay readable. */
const PORTAL_PANEL_MAX_WIDTH_PX = 36 * 16

const anchoredPanelClass = (width: "container" | "2x" = "container", direction: ReferencePickerDirection = "up") => {
  const positionClass =
    direction === "down"
      ? `absolute top-full z-40 mt-2 ${PANEL_SURFACE_CLASS}`
      : `absolute bottom-full z-40 mb-2 ${PANEL_SURFACE_CLASS}`
  return width === "2x"
    ? `${positionClass} inset-x-0 w-auto lg:left-0 lg:w-[200%] lg:max-w-none`
    : `${positionClass} inset-x-0 max-w-xl`
}

const EMBEDDED_PANEL_CLASS = "relative overflow-hidden"

const DRAWER_TRANSITION = { type: "spring", bounce: 0, duration: 0.28 } as const

const DOCK_CHROME_SELECTOR = "[data-workspace-dock-chrome]"
const STRIP_SELECTOR = "[data-reference-strip]"

function measureAnchoredPortalStyle(
  host: HTMLElement,
  direction: ReferencePickerDirection,
  widthMode: "container" | "2x",
  zIndex: number,
  anchorEl: HTMLElement | null,
): CSSProperties | null {
  const anchor = anchorEl ?? host.parentElement
  if (!anchor) return null
  const rect = anchor.getBoundingClientRect()
  const vw = window.innerWidth
  const maxWidth = Math.min(PORTAL_PANEL_MAX_WIDTH_PX, vw - VIEWPORT_MARGIN_PX * 2)
  const wide = widthMode === "2x" && window.matchMedia("(min-width: 1024px)").matches
  // Explicit anchor (e.g. + tile): fixed max width under the control, not full strip.
  const width = anchorEl
    ? maxWidth
    : wide
      ? Math.min(rect.width * 2, vw - rect.left - VIEWPORT_MARGIN_PX, maxWidth)
      : Math.min(rect.width, maxWidth)

  let left = rect.left
  if (left + width > vw - VIEWPORT_MARGIN_PX) left = vw - VIEWPORT_MARGIN_PX - width
  if (left < VIEWPORT_MARGIN_PX) left = VIEWPORT_MARGIN_PX

  const base: CSSProperties = {
    position: "fixed",
    left,
    width,
    zIndex,
    maxWidth,
  }
  if (direction === "down") {
    const pad = 12
    const maxHeight = Math.max(120, window.innerHeight - rect.bottom - GAP_PX - pad)
    return { ...base, top: rect.bottom + GAP_PX, maxHeight, overflow: "hidden", pointerEvents: "auto" }
  }
  const pad = 12
  const maxHeight = Math.max(120, rect.top - GAP_PX - pad)
  return {
    ...base,
    bottom: window.innerHeight - rect.top + GAP_PX,
    maxHeight,
    overflow: "hidden",
    pointerEvents: "auto",
  }
}

export function ReferencePickerDrawer({
  open,
  onClose,
  placement = { mode: "anchored" },
  characters,
  locations,
  props,
  existingKeys,
  onPickReference,
  onPickPrimitive,
  allowedMediaTypes = ["image", "video"],
  enableDrag = false,
  outsideClickIgnoreRefs,
  anchorRef,
  onUploadClick,
  uploadUploading = false,
  onLibraryPickClick,
  onSketchClick,
  onSketchEditRef,
  onPreviewRef,
  previewLightboxNested,
  tabs: tabsProp,
  entityPick = false,
  title,
  subtitle,
  closeOnDialogBackgroundClick = false,
  portalZIndex,
}: ReferencePickerDrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const hostRef = useRef<HTMLSpanElement>(null)
  const [portalStyle, setPortalStyle] = useState<CSSProperties | null>(null)
  const { pickerTab, setPickerTab, previewRef, setPreviewRef, tabs, pickerCandidates, pickReference } =
    useReferencePickerState({
      characters,
      locations,
      props,
      existingKeys,
      onPickReference,
      allowedMediaTypes,
      tabs: tabsProp,
      entityPick,
    })

  const isAnchored = placement.mode === "anchored"
  const isEmbedded = placement.mode === "embedded"
  const usePortal = isAnchored && portalZIndex != null
  const direction = placement.direction ?? "up"
  const widthMode = isAnchored ? (placement.width ?? "container") : "container"

  useLayoutEffect(() => {
    if (!usePortal || portalZIndex == null) {
      setPortalStyle(null)
      return
    }
    // Keep last rect while closing so AnimatePresence can finish exit.
    if (!open) return

    const update = () => {
      const host = hostRef.current
      if (!host) return
      setPortalStyle(measureAnchoredPortalStyle(host, direction, widthMode, portalZIndex, anchorRef?.current ?? null))
    }

    update()
    window.addEventListener("resize", update)
    window.addEventListener("scroll", update, true)
    const vv = window.visualViewport
    vv?.addEventListener("resize", update)
    vv?.addEventListener("scroll", update)
    return () => {
      window.removeEventListener("resize", update)
      window.removeEventListener("scroll", update, true)
      vv?.removeEventListener("resize", update)
      vv?.removeEventListener("scroll", update)
    }
  }, [open, usePortal, portalZIndex, direction, widthMode, anchorRef])

  useEffect(() => {
    if (!open) return

    const handler = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (panelRef.current?.contains(target)) return
      if (outsideClickIgnoreRefs?.some((ignoreRef) => ignoreRef.current?.contains(target))) return
      // Embedded picker lives in the bottom dock (canvas): tab/chevron chrome must not close it.
      // Anchored pickers (entity detail strip, outfit cards) should close on dock chrome click.
      if (isEmbedded && target.closest(DOCK_CHROME_SELECTOR)) return
      // Стрип — сосед дровера в layout; клик по крестику/thumb не должен закрывать picker.
      if (target.closest(STRIP_SELECTOR)) return
      // Диалоги (Библиотека, лайтбокс) живут в портале Radix и в DOM лежат вне панели —
      // без этой проверки первый же клик внутри модалки схлопывал дровер под ней.
      // Затемнение — такой же слой диалога: закрытие просмотрщика ассета кликом по backdrop
      // не должно уносить с собой дровер, из которого этот просмотрщик и открыли.
      if (!closeOnDialogBackgroundClick && insideDialogLayer(target)) return
      onClose()
    }

    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [open, onClose, outsideClickIgnoreRefs, isEmbedded, closeOnDialogBackgroundClick])

  const panelClass = usePortal
    ? cn(PANEL_SURFACE_CLASS, "flex min-h-0 flex-col")
    : isAnchored
      ? anchoredPanelClass(placement.width, direction)
      : EMBEDDED_PANEL_CLASS
  // Anchored up slides from below the panel; down slides from above — mirrors open side.
  const anchoredSlideY = direction === "down" ? -8 : 8

  const content = (
    <ReferencePickerContent
      tabs={tabs}
      pickerTab={pickerTab}
      onTabChange={setPickerTab}
      candidates={pickerCandidates}
      existingKeys={existingKeys}
      enableDrag={enableDrag}
      onClose={onClose}
      onPick={pickReference}
      onPickPrimitive={onPickPrimitive}
      previewRef={previewRef}
      onPreviewRefChange={setPreviewRef}
      onUploadClick={onUploadClick}
      uploadUploading={uploadUploading}
      onLibraryPickClick={onLibraryPickClick}
      onSketchClick={onSketchClick}
      onSketchEditRef={onSketchEditRef}
      onPreviewRef={onPreviewRef}
      previewLightboxNested={previewLightboxNested}
      padded={!isEmbedded}
      showClose={!isEmbedded}
      title={title}
      subtitle={subtitle}
      entityPick={entityPick}
      constrainToParent={usePortal}
    />
  )

  const showPanel = open && (!usePortal || portalStyle != null)

  const panel = showPanel ? (
    <motion.div
      ref={panelRef}
      key="reference-drawer"
      initial={isAnchored ? { opacity: 0, y: anchoredSlideY } : { height: 0, opacity: 0 }}
      animate={isAnchored ? { opacity: 1, y: 0 } : { height: "auto", opacity: 1 }}
      exit={isAnchored ? { opacity: 0, y: anchoredSlideY } : { height: 0, opacity: 0 }}
      transition={DRAWER_TRANSITION}
      className={panelClass}
      style={usePortal ? (portalStyle ?? undefined) : undefined}
      {...(usePortal ? { [SCENE_3D_PICKER_ATTR]: "" } : {})}
    >
      {content}
    </motion.div>
  ) : null

  const presence = <AnimatePresence initial={false}>{panel}</AnimatePresence>
  // Nested lock: body-portaled picker sits outside Dialog RemoveScroll shards — allow touch scroll.
  const portaledPresence = usePortal ? (
    <RemoveScroll enabled={open} allowPinchZoom removeScrollBar={false}>
      {presence}
    </RemoveScroll>
  ) : (
    presence
  )

  return (
    <>
      {usePortal ? <span ref={hostRef} className="hidden" aria-hidden /> : null}
      {usePortal && typeof document !== "undefined" ? createPortal(portaledPresence, document.body) : presence}
    </>
  )
}
